import React from 'react'
import type { DimensionValue, ViewStyle } from 'react-native'

import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Pressable, Stack, XStack } from '@zet/design-system'
import { isWebView } from '@zet/integration-flow/utils'
import { navigateBack, navigationRef } from '@zet/utils/navigation'

import BackIcon from '../../assets/icons/Back'
import EditIcon from '../../assets/icons/EditIcon'
import useJPBBridge from '../../hooks/useJPBBridge'
import {
  sendAnalyticsData,
  sendJfsCustomEvent,
} from '../../utils/analyticsEvents'
import { getJfsCustomerId } from '../../utils/jfsSession'
import { B14 } from '../typography'

export const IntegrationFlowHeader = ({
  totalSteps,
  currentStep,
  setShowEditBasicDetailsSheet,
  disableBackButton = false,
}: {
  setShowEditBasicDetailsSheet?: (arg: boolean) => void
  totalSteps?: number
  currentStep?: number
  disableBackButton?: boolean
}) => {
  const { goBack } = useJPBBridge()
  const { customerId: storeCustomerId } = useAddCustomerStore()

  const handleOnPress = () => {
    if (isWebView() && !navigationRef.canGoBack()) goBack()
    else navigateBack()
  }

  const getProgressWidth = (): DimensionValue => {
    if (!totalSteps || !currentStep) {
      return '0%'
    }

    const progress = Math.min(
      Math.max(Math.round((currentStep / totalSteps) * 100), 0),
      100,
    )

    return `${progress}%`
  }

  const handleShowEditBasicDetailsSheet = () => {
    setShowEditBasicDetailsSheet?.(true)
    sendAnalyticsData({ type: 'click', screen: 'EDITDETAIL_CTA' })
    sendJfsCustomEvent('edit_info', {
      customer_id: getJfsCustomerId() || storeCustomerId || '',
      click_text: 'Edit details',
    })
  }

  return (
    <XStack className="items-center justify-between bg-white">
      <Pressable
        className="bg-white p-3"
        onPress={handleOnPress}
        disabled={disableBackButton}
      >
        <BackIcon />
      </Pressable>
      {totalSteps && currentStep && (
        <Stack className="h-2 w-[calc(40%)] bg-grays-30 rounded-full right-2">
          <Stack
            className="h-full rounded-full bg-primary-150"
            style={{ width: getProgressWidth() } as ViewStyle}
          />
        </Stack>
      )}
      {setShowEditBasicDetailsSheet ? (
        <Pressable
          className="m-3 select-none flex-row items-center rounded-full bg-[#EBECEF] px-3 py-2"
          onPress={handleShowEditBasicDetailsSheet}
        >
          <EditIcon />
          <B14 className="ml-2 font-bold">{'Edit details'}</B14>
        </Pressable>
      ) : (
        <Stack className="px-3 py-2" />
      )}
    </XStack>
  )
}
